import { useContextBroker } from '../context/ContextBroker/useContextBroker';
import { useUser } from '../hook/user';
import { USER_STATUS } from '@/types/User';
import { CLOSET_SLOT_TIER_SLUG } from '@/types/Closet';
import { useCloset } from './closet';
import { SignupState } from '@/types/Signup';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';

export interface EllaAnalyticsMetadata {
  userState: USER_STATUS | null | undefined;
  membershipTier: string;
  addOns: boolean;
  canBorrow: boolean;
  loggedInState: boolean;
}

export interface SignupAnalyticsMetadata {
  membershipTier: string[];
  addOn: boolean;
  insuranceCoverage: string;
}

export const useEllaAnalytics = (): { metadata: EllaAnalyticsMetadata } => {
  const { value: user } = useUser();
  const {
    value: { closet }
  } = useCloset();
  const slots = closet?.slots || [];
  const hasPremierSlot = slots.some(
    (slot) => slot.maxTier === CLOSET_SLOT_TIER_SLUG.PREMIER
  );
  const hasNonPremierSlot = slots.some(
    (slot) => slot.maxTier !== CLOSET_SLOT_TIER_SLUG.PREMIER
  );

  const isPastDue = closet?.error?.status === 402;

  let membershipTier = 'no_tier';

  if (closet?.subscriptionPlanProducts) {
    // Sort by more expensive tiers to least
    const subs = closet?.subscriptionPlanProducts.sort((plan1, plan2) => {
      return plan2.price.asNumber - plan1.price.asNumber;
    });
    membershipTier = `${subs?.[0]?.name} Member`;
  }

  return {
    metadata: {
      userState: isPastDue ? USER_STATUS.PAST_DUE : user?.status,
      membershipTier,
      addOns: hasPremierSlot && hasNonPremierSlot,
      canBorrow: slots.some((slot) => slot.isOpen) ? true : false,
      loggedInState: !!user
    }
  };
};

export const useSignupAnalytics = (
  user: SignupState
): { metadata: SignupAnalyticsMetadata } => {
  return {
    metadata: {
      membershipTier: (user?.plan && user?.plan?.closetAccess) || [],
      addOn: !!user.addon_subscription,
      insuranceCoverage: user.insurance_level
    }
  };
};

export interface ReactivateAnalyticsMetadata {
  membership_tier: string;
  add_ons: boolean;
  insurance_coverage: string;
}

export const useReactivateAnalytics = (
  subscriptionPlans?: SubscriptionProduct[],
  insurancePlans?: InsuranceProduct[]
): { metadata: ReactivateAnalyticsMetadata } => {
  // Order subscriptionPlans by price descending,
  // so if member has Premier Add-on, it's always second
  const sortedPlans = subscriptionPlans
    ? [...subscriptionPlans].sort((a, b) => {
        return b.price.asNumber - a.price.asNumber;
      })
    : [];

  const membershipTier = sortedPlans?.[0]?.name ?? '';
  const hasAddOns =
    sortedPlans.length > 1 && sortedPlans[1]?.name === 'Premier';
  const insuranceCoverage = insurancePlans?.[0]?.name ?? '';

  return {
    metadata: {
      membership_tier: membershipTier,
      add_ons: hasAddOns,
      insurance_coverage: insuranceCoverage
    }
  };
};
